/**
 * P1-F: 可视化编排编辑器 — React Flow 节点式工作流
 * 节点类型：trigger / condition / llm / tool / output
 * 支持拖拽连线、保存、运行、查看执行结果。
 */
import { useCallback, useEffect, useState } from 'react';
import ReactFlow, { Background, Controls, addEdge, useNodesState, useEdgesState, Connection, Handle, Position } from 'reactflow';
import 'reactflow/dist/style.css';
import { apiFetch } from '../services/apiClient';
import { Play, Save, Plus, Trash2, Loader2, Workflow as WorkflowIcon } from 'lucide-react';

const NODE_LABELS: Record<string, string> = {
  trigger: '触发器', condition: '条件', llm: 'LLM 调用', tool: '工具', output: '输出',
};
const NODE_COLORS: Record<string, string> = {
  trigger: 'border-emerald-400/40 bg-emerald-400/10',
  condition: 'border-amber-400/40 bg-amber-400/10',
  llm: 'border-sky-400/40 bg-sky-400/10',
  tool: 'border-orange-400/40 bg-orange-400/10',
  output: 'border-violet-400/40 bg-violet-400/10',
};

interface WfNodeData {
  type: string;
  config: Record<string, any>;
  tools?: { name: string; description: string }[];
  onChange?: (id: string, config: Record<string, any>) => void;
}

function FlowNode({ id, data }: { id: string; data: WfNodeData }) {
  const color = NODE_COLORS[data.type] || NODE_COLORS.llm;
  const cfg = data.config || {};
  return (
    <div className={`rounded-lg border px-3 py-2 ${color}`} style={{ minWidth: 180 }}>
      {data.type !== 'trigger' && <Handle type="target" position={Position.Left} />}
      <div className="text-[11px] font-black uppercase tracking-wider text-white/70">{NODE_LABELS[data.type] || data.type}</div>
      {data.type === 'trigger' && (
        <input value={cfg.input || ''} placeholder="初始输入" onChange={e => data.onChange?.(id, { ...cfg, input: e.target.value })} className="mt-1 w-full rounded border border-white/10 bg-black/30 px-2 py-1 text-[11px] text-white" />
      )}
      {data.type === 'llm' && (
        <textarea value={cfg.prompt || ''} placeholder="提示词" rows={2} onChange={e => data.onChange?.(id, { ...cfg, prompt: e.target.value })} className="mt-1 w-full rounded border border-white/10 bg-black/30 px-2 py-1 text-[11px] text-white" />
      )}
      {data.type === 'tool' && (
        <select value={cfg.tool || ''} onChange={e => data.onChange?.(id, { ...cfg, tool: e.target.value })} className="mt-1 w-full rounded border border-white/10 bg-black/30 px-2 py-1 text-[11px] text-white">
          <option value="">选择工具</option>
          {(data.tools || []).map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
        </select>
      )}
      {data.type === 'condition' && (
        <input value={cfg.keyword || ''} placeholder="包含关键词" onChange={e => data.onChange?.(id, { ...cfg, keyword: e.target.value, operator: cfg.operator || 'contains' })} className="mt-1 w-full rounded border border-white/10 bg-black/30 px-2 py-1 text-[11px] text-white" />
      )}
      {data.type === 'output' && (
        <input value={cfg.template || ''} placeholder="输出模板 {{input}}" onChange={e => data.onChange?.(id, { ...cfg, template: e.target.value })} className="mt-1 w-full rounded border border-white/10 bg-black/30 px-2 py-1 text-[11px] text-white" />
      )}
      {data.type !== 'output' && <Handle type="source" position={Position.Right} />}
    </div>
  );
}

const nodeTypes = { wfNode: FlowNode };

export function WorkflowEditor({ t }: { t: any }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [tools, setTools] = useState<{ name: string; description: string }[]>([]);
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [currentId, setCurrentId] = useState<string>('');
  const [name, setName] = useState('未命名工作流');
  const [msg, setMsg] = useState('');
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<any>(null);
  const [runInput, setRunInput] = useState('');

  const onConnect = useCallback((conn: Connection) => {
    setEdges(eds => addEdge({ ...conn, id: `e_${Date.now()}` }, eds));
  }, [setEdges]);

  const updateNodeConfig = useCallback((id: string, config: Record<string, any>) => {
    setNodes(nds => nds.map(n => n.id === id ? { ...n, data: { ...n.data, config } } : n));
  }, [setNodes]);

  // 注入 tools 与 onChange 到节点 data
  const enrichedNodes = nodes.map(n => ({ ...n, data: { ...n.data, tools, onChange: updateNodeConfig } }));

  const loadTools = useCallback(async () => {
    try {
      const res = await apiFetch('/api/workflows/tools', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setTools(data.tools || []);
    } catch { /* ignore */ }
  }, []);

  const loadWorkflows = useCallback(async () => {
    try {
      const res = await apiFetch('/api/workflows', { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setWorkflows(data.workflows || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { void loadTools(); void loadWorkflows(); }, [loadTools, loadWorkflows]);

  const addNode = useCallback((type: string) => {
    const id = `n_${Date.now()}`;
    setNodes(nds => [...nds, { id, type: 'wfNode', position: { x: 100 + nds.length * 60, y: 100 + nds.length * 40 }, data: { type, config: {} } }]);
  }, [setNodes]);

  const handleSave = useCallback(async () => {
    setMsg('');
    try {
      const payload = {
        id: currentId || undefined,
        name,
        nodes: nodes.map(n => ({ id: n.id, type: n.data.type, position: n.position, config: n.data.config || {} })),
        edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target, label: e.label })),
      };
      const res = await apiFetch('/api/workflows', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '保存失败');
      setCurrentId(data.id);
      setMsg('已保存');
      await loadWorkflows();
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  }, [currentId, name, nodes, edges, loadWorkflows]);

  const handleRun = useCallback(async () => {
    if (!currentId) { setMsg('请先保存工作流'); return; }
    setRunning(true); setMsg(''); setRunResult(null);
    try {
      const res = await apiFetch(`/api/workflows/${currentId}/run`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ input: runInput }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || '执行失败');
      setRunResult(data);
    } catch (e: unknown) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  }, [currentId, runInput]);

  const handleLoad = useCallback((wf: any) => {
    setCurrentId(wf.id);
    setName(wf.name);
    setNodes((wf.nodes || []).map((n: any) => ({ id: n.id, type: 'wfNode', position: n.position || { x: 100, y: 100 }, data: { type: n.type, config: n.config || {} } })));
    setEdges((wf.edges || []).map((e: any) => ({ id: e.id, source: e.source, target: e.target, label: e.label })));
    setRunResult(null);
  }, [setNodes, setEdges]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-white/10 bg-white/[0.03] px-5 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-cyan-300"><WorkflowIcon size={15} /> {t?.workflowTitle || '可视化编排'}</div>
          <div className="flex items-center gap-1.5">
            {(['trigger', 'condition', 'llm', 'tool', 'output'] as const).map(tp => (
              <button key={tp} type="button" onClick={() => addNode(tp)} className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-bold text-white/70 hover:bg-white/10"><Plus size={11} /> {NODE_LABELS[tp]}</button>
            ))}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input value={name} onChange={e => setName(e.target.value)} className="rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white" placeholder="工作流名称" />
          <button type="button" onClick={() => void handleSave()} className="inline-flex items-center gap-1 rounded-md border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1.5 text-[11px] font-bold text-emerald-200 hover:bg-emerald-400/20"><Save size={12} /> 保存</button>
          <input value={runInput} onChange={e => setRunInput(e.target.value)} className="rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white" placeholder="运行输入（可选）" />
          <button type="button" onClick={() => void handleRun()} disabled={running} className="inline-flex items-center gap-1 rounded-md border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-1.5 text-[11px] font-bold text-cyan-200 hover:bg-cyan-400/20 disabled:opacity-50">{running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} 运行</button>
          {msg && <span className="text-[11px] text-cyan-200">{msg}</span>}
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[1fr_260px]">
        <div className="min-h-0">
          <ReactFlow nodes={enrichedNodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect} nodeTypes={nodeTypes} fitView>
            <Background />
            <Controls />
          </ReactFlow>
        </div>
        <aside className="min-h-0 space-y-3 overflow-y-auto border-l border-white/10 bg-black/20 p-3 custom-scrollbar">
          <div>
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-white/40">已有工作流</div>
            {workflows.length === 0 && <div className="text-[11px] text-white/30">暂无，编排后保存</div>}
            {workflows.map(wf => (
              <button key={wf.id} type="button" onClick={() => handleLoad(wf)} className={`mb-1 w-full truncate rounded-md border px-2 py-1.5 text-left text-[11px] ${wf.id === currentId ? 'border-cyan-400/30 bg-cyan-400/10 text-cyan-200' : 'border-white/8 bg-white/[0.03] text-white/60 hover:bg-white/[0.06]'}`}>{wf.name}</button>
            ))}
          </div>
          {runResult && (
            <div>
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-white/40">执行结果</div>
              <div className={`rounded-md border px-2 py-1.5 text-[11px] ${runResult.ok ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200' : 'border-red-400/30 bg-red-400/10 text-red-200'}`}>
                {runResult.ok ? '成功' : '失败'} · {runResult.durationMs}ms
              </div>
              {runResult.finalOutput && <div className="mt-1 whitespace-pre-wrap rounded-md border border-white/10 bg-white/5 px-2 py-1.5 text-[11px] text-white/70">{runResult.finalOutput}</div>}
              {runResult.error && <div className="mt-1 text-[11px] text-red-300">{runResult.error}</div>}
              {runResult.nodeResults && runResult.nodeResults.map((nr: any) => (
                <div key={nr.nodeId} className="mt-1 text-[10px] text-white/40">{nr.type}: {nr.ok ? String(nr.output || '').slice(0, 60) : nr.error}</div>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
